import {
  APIReportFilters,
  ArdoqId,
  FilterExpression,
  ReportColumn,
} from '@ardoq/api-types';
import { Maybe } from '@ardoq/common-helpers';
import {
  EnhancedSearchResponse,
  getLoadPresentationReportColumnSuggestionsAsync,
  getLoadReportColumnSuggestionsAsync,
  ReportEventLocations,
  ReportPageAndPagination,
  ReportReaderCommands,
  ReportSort,
  ReportTrackingEvents,
} from '@ardoq/report-reader';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  allFiltersCleared,
  filterValueChanged,
  filterValueRemoved,
  pageAndPaginationChanged,
  sortingChanged,
} from '../actions';
import { getExportButtonCommands } from './exportButtonCommands';
import { trackEvent } from 'tracking/tracking';

const setFilterExpression = (
  column: ReportColumn,
  expression: Maybe<FilterExpression[] | FilterExpression>,
  eventLocation: ReportEventLocations
) => {
  dispatchAction(filterValueChanged({ columnKey: column.key, expression }));
  trackEvent(ReportTrackingEvents.FILTERED_REPORT_READER, {
    from: eventLocation,
    filterDataType: column.dataType,
  });
};
const addEmptyFilterExpression = (column: ReportColumn) => {
  dispatchAction(
    filterValueChanged({ columnKey: column.key, expression: null })
  );
};
const removeFilterExpression = (
  columnKey: ArdoqId,
  eventLocation: ReportEventLocations
) => {
  dispatchAction(filterValueRemoved(columnKey));
  trackEvent(ReportTrackingEvents.FILTER_REMOVED, {
    from: eventLocation,
  });
};

const setPageAndPagination = (pageAndPagination: ReportPageAndPagination) =>
  dispatchAction(pageAndPaginationChanged(pageAndPagination));
const setSort = (sort: ReportSort) => dispatchAction(sortingChanged(sort));
const clearAllFilters = () => dispatchAction(allFiltersCleared());

export const getReportReaderCommands = ({
  filters,
  reportId,
  searchResults,
  presentationId,
  slideId,
  eventLocation,
}: {
  filters: APIReportFilters | null;
  reportId: string;
  searchResults: Maybe<EnhancedSearchResponse>;
  eventLocation: ReportEventLocations;
  presentationId?: ArdoqId;
  slideId?: Maybe<ArdoqId>;
}): ReportReaderCommands => {
  return {
    setPageAndPagination,
    setSort,
    clearAllFilters,
    setFilterExpression: (column, expression) =>
      setFilterExpression(column, expression, eventLocation),
    addEmptyFilterExpression,
    removeFilterExpression: columnKey =>
      removeFilterExpression(columnKey, eventLocation),
    loadComponentSuggestions:
      presentationId && slideId
        ? getLoadPresentationReportColumnSuggestionsAsync(
            reportId,
            slideId,
            presentationId
          )
        : getLoadReportColumnSuggestionsAsync(reportId),
    ...getExportButtonCommands(reportId, searchResults, filters),
  };
};
